import { doGet } from "../modules/DataManager";
import history from "./history";
import store from "./store";

const permissions = {
    'Utenti': ['admin', 'b2b', 'cdi'],
    'Prenotazione': ['admin', 'b2b'],
    'prenotazione': ['admin', 'b2b'],
    'Listino': ['admin', 'b2b'],
    'listino': ['admin', 'b2b'],
    'storico': ['admin', 'b2b', 'cdi', 'user'],
    'Assenze': ['admin'],
    'Contratti': ['admin', 'b2b'],
    'Fields': ['admin'],
    'Mappa': ['admin', 'b2b', 'cdi'],
    'giri_cdi': ['admin', 'cdi'],
    'cdi_absences': ['admin', 'cdi'],
    'options': ['admin', 'b2b', 'cdi'],
}

function getState() {
    return store.getState()
}

export function goTo(path) {
    history.push(path);
};

export function goBack() {
    history.goBack();
};

function getUserRoles(state) {
    let roles = ['user'];
    if (state.user?.company?.types) {
        roles = [...roles, ...state.user.company.types.map((t) => t?.id ? t.id : t)];
    }
    if (state.user?.role) {
        Object.keys(state.user.role).filter(k => state.user.role[k]).forEach(k => roles.push(k));
    }
    return roles;
}

export function canView(functionality) {
    const state = getState();
    const roles = getUserRoles(state);
    return permissions[functionality]?.some(r => roles.includes(r)) || false;
}

// Companies with the Trenord-specific trip history (/storico-trenord).
const TRENORD_COMPANIES = ['TRENORD', 'DEMO-TRENORD'];

export function isTrenordCompany(company) {
    return !!company && typeof company === 'object' && TRENORD_COMPANIES.includes(company.code);
}

// Where the "Viaggi" menu entry leads for the logged company.
export function historyPath(company) {
    return isTrenordCompany(company) ? '/storico-trenord' : '/storico';
}

export async function getCompany(id) {
    return await doGet("/companies/get-company", {
        'id': id
    })
}

export function getContractsByCompany(company) {
    return new Promise((resolve) => {
        const contracts = [];
        if (!company) {
            resolve(contracts);
        }
        if (company && typeof company !== "string") {
            company = company?.code;
        }
        if (company && company !== "") {
            window.firebase.firestore().collection("contratti")
                .where("company", "==", company)
                .get()
                .then((snap) => {
                    if (!snap.empty) {
                        for (let doc of snap.docs) {
                            const contratto = doc.data();
                            contracts.push(contratto);
                        }
                    }
                    resolve(contracts);
                })
                .catch((err) => {
                    console.error(err);
                    resolve(contracts);
                })
        } else {
            resolve(contracts);
        }
    });
};

// Cache semplice per geocoding
const geocodeCache = new Map();

const geocodeQueue = [];
let isGeocoding = false;

async function processGeocodeQueue() {
    if (isGeocoding || geocodeQueue.length === 0) return;
    isGeocoding = true;

    const item = geocodeQueue.shift();
    const { search, resolve } = item;
    let { retryCount = 0 } = item;

    try {
        const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(search)}&limit=1`;
        const res = await fetch(url, {
            headers: {
                'Accept': 'application/json',
                'User-Agent': 'MBility-B2B-App'
            }
        });

        if (res.status === 425) {
            console.warn("Nominatim rate limit hit (425). Retrying later...", search);
            if (retryCount < 3) {
                geocodeQueue.unshift({ search, resolve, retryCount: retryCount + 1 });
                setTimeout(() => {
                    isGeocoding = false;
                    processGeocodeQueue();
                }, 2000);
            } else {
                resolve([]);
                setTimeout(() => {
                    isGeocoding = false;
                    processGeocodeQueue();
                }, 1100);
            }
            return;
        }

        const data = await res.json();

        if (data && data.length > 0) {
            const results = data.map(item => ({
                geometry: {
                    location: {
                        lat: parseFloat(item.lat),
                        lng: parseFloat(item.lon)
                    }
                },
                formatted_address: item.display_name
            }));
            geocodeCache.set(search, results);
            resolve(results);
        } else {
            resolve([]);
        }
    } catch (err) {
        if (err.message === 'Failed to fetch' || err.message.includes('425') || err.message.includes('fetch')) {
            console.warn("Nominatim fetch blocked (425/CORS). Retrying later...", search);
            if (retryCount < 3) {
                geocodeQueue.unshift({ search, resolve, retryCount: retryCount + 1 });
                setTimeout(() => {
                    isGeocoding = false;
                    processGeocodeQueue();
                }, 2000);
                return;
            }
        }
        console.error("Nominatim geocode error:", err);
        resolve([]);
    }

    // Wait ~1.1 second before processing the next item to respect OSM policy (1 req/sec max)
    setTimeout(() => {
        isGeocoding = false;
        processGeocodeQueue();
    }, 1100);
}

/**
 * Geocoding con Nominatim (OpenStreetMap)
 * Converte la risposta in formato compatibile con Google Maps per retrocompatibilità
 */
export async function geocode(search) {
    if (!search) return Promise.resolve([]);

    // Check cache
    if (geocodeCache.has(search)) {
        return Promise.resolve(geocodeCache.get(search));
    }

    return new Promise((resolve) => {
        geocodeQueue.push({ search, resolve, retryCount: 0 });
        processGeocodeQueue();
    });
}

export function formatUserName(name) {
    if (!name) return "";
    return name.toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
}